/**
 * F10 — Interior Execution service module (v2).
 * 10 axiosInstance wrappers 1:1 with PART 1 §1–§7 (contract v2).
 * Paths UNCHANGED from v1; field renames: statusTypeId→wbsStageId, +scope.
 * Axios interceptor returns response.data bare — never wrap in .data.
 * camelCase params throughout (QueryAlias on backend mirrors these).
 */
import axiosInstance from "../api/axios";

// §1 (getProjectWbs) removed — the Construction WBS screen was folded into
// the WBS Status tab (BuildingStatus), which derives its matrix from
// /projects/buildings/status instead.

// §2 — Unit readiness KPIs
export const getUnitReadiness = (propertyId) =>
  axiosInstance.get(`/interior/unit/${propertyId}/readiness`);

// §3 — Unit execution stages (status chips, blockingWbsStageId, sub-phases)
export const getUnitExecutionStages = (propertyId) =>
  axiosInstance.get(`/interior/unit/${propertyId}/execution-stages`);

// §4 — Procurement timeline
export const getUnitProcurementTimeline = (propertyId) =>
  axiosInstance.get(`/interior/unit/${propertyId}/procurement-timeline`);

// §5 — Conflicts (ruleId/severity/dates)
export const getUnitConflicts = (propertyId) =>
  axiosInstance.get(`/interior/unit/${propertyId}/conflicts`);

// §6 — Forecast (prerequisiteWbsStageId, nullable dates)
export const getUnitForecast = (propertyId) =>
  axiosInstance.get(`/interior/unit/${propertyId}/forecast`);

// §7 — Execution-plan CRUD (developer-only writes)
export const getExecutionPlan = (params = {}) =>
  axiosInstance.get("/interior/execution-plan", { params });

export const createExecutionStage = (body) =>
  axiosInstance.post("/interior/execution-plan", body);

export const updateExecutionStage = (id, body) =>
  axiosInstance.put(`/interior/execution-plan/${id}`, body);

export const deleteExecutionStage = (id) =>
  axiosInstance.delete(`/interior/execution-plan/${id}`);

/* ---------------- Two-tier ID execution plans (v3_27) ---------------- */

// Tier-1 derived skeleton (urgency device shown right after a sale)
export const getSkeletonPlan = (propertyId) =>
  axiosInstance.get(`/interior/unit/${propertyId}/skeleton-plan`);

// Tier-2 persisted plan (BV associate generates on demand after developer approval)
export const generateExecutionPlan = (idLeadId, body, regenerate = false) =>
  axiosInstance.post(`/interior/leads/${idLeadId}/execution-plan?regenerate=${regenerate}`, body);

export const getIdExecutionPlan = (idLeadId) =>
  axiosInstance.get(`/interior/leads/${idLeadId}/execution-plan`);

export const advancePlanStage = (stageId) =>
  axiosInstance.put(`/interior/execution-plan/stages/${stageId}/advance`);

// achieved_date: "YYYY-MM-DD"; file optional (multipart)
export const achievePlanStage = (stageId, achievedDate, file = null) => {
  const fd = new FormData();
  fd.append("achieved_date", achievedDate);
  if (file) fd.append("completion_image", file);
  return axiosInstance.put(`/interior/execution-plan/stages/${stageId}/achieve`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const unskipPlanStage = (stageId) =>
  axiosInstance.put(`/interior/execution-plan/stages/${stageId}/unskip`);

export const refreshExecutionPlan = (idLeadId) =>
  axiosInstance.put(`/interior/leads/${idLeadId}/execution-plan/refresh`);

/* ---------------- Package architecture (v3_29) ---------------- */

// Global 18-package catalog (merged with per-phase overrides when projectPhaseId given)
export const getExecutionPackages = (projectPhaseId = null) =>
  axiosInstance.get("/interior/execution-packages", {
    params: projectPhaseId ? { projectPhaseId } : {},
  });

// Per-unit package configuration (all packages with include/mode/anchor/complexity state)
export const getPackageConfig = (idLeadId) =>
  axiosInstance.get(`/interior/leads/${idLeadId}/package-config`);

// Save BV decisions; impact rules derive secondary packages (returned with warnings)
export const savePackageConfig = (idLeadId, items) =>
  axiosInstance.put(`/interior/leads/${idLeadId}/package-config`, { items });

// Computed execution strategy for the current config (grouped, with feasibility warnings)
export const getStrategyPreview = (idLeadId) =>
  axiosInstance.get(`/interior/leads/${idLeadId}/strategy-preview`);

// P1: Interior Execution section — developer-approved units ready for execution.
// params: { projectId?, propertyConfigId?, search?, page?, pageSize? }
export const getExecutionUnits = (params = {}) =>
  axiosInstance.get(`/interior/execution-units`, { params });

// P1: approved BOQ lines grouped per execution package (cumulative across rooms)
export const getPackageBoq = (idLeadId) =>
  axiosInstance.get(`/interior/leads/${idLeadId}/package-boq`);

/* ---------------- Move-in Service config (buyer-requested; BV-configured) ---------------- */
// The lead's move-in config (null until configured). { bhk, isIntercity, moveInDateStart/End,
// sourceAddress, destinationAddress, notes } — bhk + isIntercity are derived server-side.
export const getMoveInConfig = (idLeadId) =>
  axiosInstance.get(`/interior/leads/${idLeadId}/move-in-config`);
// Save source/destination + move window; bhk (from flat config) + isIntercity are derived.
export const saveMoveInConfig = (idLeadId, body) =>
  axiosInstance.put(`/interior/leads/${idLeadId}/move-in-config`, body);

/* ---------------- Pre-tender BOQ→SKU/brand mapping (v3_54) ---------------- */
// Approved BOQ grouped per package with each line's BV-assigned SKU/brand mapping + all_mapped gate.
export const getBoqSkuMapping = (idLeadId) =>
  axiosInstance.get(`/interior/leads/${idLeadId}/boq-sku-mapping`);
// Assign/update the concrete SKU + brand (free text) for one BOQ line.
export const upsertBoqSkuMapping = (idLeadId, boqLineId, body) =>
  axiosInstance.put(`/interior/leads/${idLeadId}/boq-sku-mapping/${boqLineId}`, body);

// Buyer-shareable BOQ of the chosen SKUs for awarded items (no pricing).
export const getChosenSkuBoq = (idLeadId) =>
  axiosInstance.get(`/interior/leads/${idLeadId}/chosen-sku-boq`);

// P4: purchase orders
export const getVendorOptions = (stageId) =>
  axiosInstance.get(`/interior/execution-plan/stages/${stageId}/vendor-options`);
// Items (winning tender quote lines) a vendor won in this stage — auto-fills the raise-PO form.
export const getPoPrefill = (stageId, vendorId) =>
  axiosInstance.get(`/interior/execution-plan/stages/${stageId}/po-prefill`, { params: { vendorId } });
export const getPurchaseOrders = (stageId) =>
  axiosInstance.get(`/interior/execution-plan/stages/${stageId}/purchase-orders`);
export const raisePurchaseOrder = (stageId, body) =>
  axiosInstance.post(`/interior/execution-plan/stages/${stageId}/purchase-orders`, body);
export const updatePurchaseOrder = (poId, body) =>
  axiosInstance.put(`/interior/purchase-orders/${poId}`, body);
export const sendPurchaseOrder = (poId) =>
  axiosInstance.post(`/interior/purchase-orders/${poId}/send`);

// P3: complete / skip / record a Phase-Gate task (multipart — optional evidence file).
// fields: { status, recordedDate?, notes?, metadataJson? (object) }, evidence: File|null
export const updateExecutionTask = (taskId, fields, evidence = null) => {
  const fd = new FormData();
  fd.append("status", fields.status);
  if (fields.recordedDate) fd.append("recorded_date", fields.recordedDate);
  if (fields.notes) fd.append("notes", fields.notes);
  if (fields.metadataJson) fd.append("metadata_json", JSON.stringify(fields.metadataJson));
  if (evidence) fd.append("evidence", evidence);
  return axiosInstance.put(`/interior/execution-plan/tasks/${taskId}`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

// Replace the buyer-facing installment breakdown of a stage (v3_34).
// items: [{ chargeType, description?, amount }] — Σ amounts must equal the installment.
export const saveStageCharges = (stageId, items) =>
  axiosInstance.put(`/interior/execution-plan/stages/${stageId}/charges`, { items });

// Rendered receipt HTML for the Record-Installment preview (v3_33) — the same
// letterhead body that gets signed + emailed to the buyer on Record & Unlock.
export const getInstallmentReceiptPreview = (stageId, { amount, reference, paymentDate }) =>
  axiosInstance.get(`/interior/execution-plan/stages/${stageId}/installment-receipt-preview`, {
    params: { amount, reference, paymentDate },
  });

// Per-package (per-stage) buyer cost estimate BOQ (v3_51, FYI — no approval).
// getStagePackageBoq: this stage's charge aggregation + FYI status + signed PDF url when generated.
export const getStagePackageBoq = (stageId) =>
  axiosInstance.get(`/interior/execution-plan/stages/${stageId}/package-boq`);
// generate + sign + share with the buyer as an FYI (status → SENT); requires the package priced.
export const sendStagePackageBoq = (stageId) =>
  axiosInstance.post(`/interior/execution-plan/stages/${stageId}/package-boq`);

// EXECUTION_STAGE installment bound to a plan stage (unlocks its procurement)
export const recordStageInstallment = (idLeadId, stageId, amount, reference, paymentDate) =>
  axiosInstance.post(`/interior-designers/id-lead-payments`, [
    {
      idLeadId,
      paymentType: "EXECUTION_STAGE",
      paymentAmount: amount,
      paymentDate,
      paymentReference: reference,
      idExecutionPlanStageId: stageId,
    },
  ]);
